/*
 * Copyright (c) 2026. Tooling Team
 */

import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { EditableTableCell } from "@uhg-abyss/web/ui/DataTable";
import { formatCellValue } from "../../utils/formatters";

const USER_COMMENT_CELL_STYLE = {
  maxWidth: "100%",
  minWidth: 0,
  width: "100%",
};

const USER_COMMENT_TEXT_STYLE = {
  display: "block",
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: "100%",
};

const useIsOverflowing = (value) => {
  const ref = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const updateOverflow = () => {
      setIsOverflowing(element.scrollWidth > element.clientWidth);
    };

    updateOverflow();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateOverflow);
      return () => window.removeEventListener("resize", updateOverflow);
    }

    const resizeObserver = new ResizeObserver(updateOverflow);
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, [value]);

  return [ref, isOverflowing];
};

const UserCommentsCell = (cell) => {
  const commentValue = cell.getValue?.() ?? "";
  const commentText = formatCellValue(commentValue);
  const [commentRef, isOverflowing] = useIsOverflowing(commentText);
  const editActions = cell.table?.options?.meta?.editActions;
  const isRowInEditMode = editActions?.rowsInEditMode?.[cell.row?.id];

  if (isRowInEditMode) {
    return <EditableTableCell {...cell} inputType="text" />;
  }

  const commentTextElement = (
    <div
      aria-label="User comment"
      ref={commentRef}
      style={USER_COMMENT_TEXT_STYLE}
      title={isOverflowing ? commentText : undefined}
    >
      {commentText}
    </div>
  );

  return <div style={USER_COMMENT_CELL_STYLE}>{commentTextElement}</div>;
};

UserCommentsCell.propTypes = {
  getValue: PropTypes.func,
  row: PropTypes.object,
  table: PropTypes.object,
};

export default UserCommentsCell;
